import React, { useState } from 'react';
import { ArrowRight, CalendarClock, Gauge, PieChart as PieIcon, Telescope, TrendingUp } from 'lucide-react';
import {
  DEFAULT_MIX_STAGE,
  bpToPct,
  dayLabel,
  foldSegments,
  monthLabel,
  type Cell,
  type DealMixData,
  type ForecastData,
  type MixStage,
  type MixWheel,
  type PlannedData,
  type SalesAnalyticsResponse,
  type SettlementTier,
  type VelocityData,
  type VelocityRow,
  type VelocityTrendData,
} from '../../../hooks/pipelineTracker/salesAnalyticsTypes';
import { Honesty, Panel, Pill, SURFACE, money, num, type ComingSoonItem } from './pipelineParts';
import { CHART_PALETTE, chartColour } from './trackerKit';
import { Donut, StackedMonths } from './SalesCharts';
import VelocityTrendChart from './VelocityTrendChart';
import { SkeletonTable } from '../../../components/common/SkeletonLoader';

/**
 * SalesTrekker V6, phase 3: Deal mix at settlement, Pipeline velocity, Pipeline velocity trend,
 * Pipeline forecast and Planned settlements.
 *
 * EVERY FIGURE COMES FROM THE salesAnalyticsReport PAYLOAD. There is no sample value, placeholder
 * or fallback figure in this file. The design mockup's numbers were Matt Spears' mock values and
 * none of them is here; only its layout, labels and behaviour are.
 *
 * Each section states its own basis beside its figures: what window, which deals, and what the
 * number cannot say. A velocity with no denominator, or a wheel with no coverage line, reads as a
 * measurement of the whole book when it is a measurement of the part that carries the field.
 */

type Mode = '$' | '#';

/**
 * What every panel here takes from the tab: its group's accent (the jump-bar colour) and the scope
 * chip saying whether the period drives it or the snapshot date does.
 */
export interface PanelScope {
  accent?: string;
  chip?: React.ReactNode;
}

const NOT_RECORDED_COLOUR = '#4b5563';

export const UnitToggle: React.FC<{ mode: Mode; onChange: (m: Mode) => void; label: string }> = ({ mode, onChange, label }) => (
  <div className="inline-flex rounded-lg border border-gray-700 bg-gray-900/60 p-0.5" role="group" aria-label={label}>
    {(['$', '#'] as const).map((m) => (
      <button
        key={m}
        type="button"
        aria-pressed={m === mode}
        aria-label={m === '$' ? 'Dollars' : 'Deal counts'}
        onClick={() => onChange(m)}
        className={`rounded-md px-2.5 py-1 text-[0.6875rem] font-bold transition-colors ${
          m === mode ? 'bg-blue-600 text-white' : 'text-gray-400 hover:bg-white/[0.06] hover:text-gray-200'
        }`}
      >
        {m}
      </button>
    ))}
  </div>
);

const WindowLine: React.FC<{ from: string; to: string }> = ({ from, to }) => (
  <span className="tabular-nums">
    {dayLabel(from)}
    <ArrowRight className="mx-1.5 inline h-3 w-3 text-gray-600" />
    {dayLabel(to)}
  </span>
);

const cellText = (c: Cell, mode: Mode): string => (mode === '$' ? money(c.valueCents) : num(c.count));
const cellSub = (c: Cell, mode: Mode): string =>
  (mode === '$' ? `${num(c.count)} ${c.count === 1 ? 'deal' : 'deals'}` : money(c.valueCents));

const TH = 'px-3 py-2 text-[0.6875rem] font-semibold uppercase tracking-wider text-gray-400';
const TD = 'px-3 py-2 tabular-nums';

// ---------------------------------------------------------------------------------------------
// Deal mix at settlement
// ---------------------------------------------------------------------------------------------

/**
 * The stage choice on Deal mix, in the movements row's own words and order (Matt Spears' Sales
 * mock up). Only the four stages the export dates: Meetings, Analysis and Proposals join when
 * Salestrekker dates them, and until then the tab's "Coming when the data is available" list says
 * why they are absent (UNDATED_STAGES_ITEM).
 */
export const MIX_STAGE_OPTIONS: ReadonlyArray<{ key: MixStage; label: string }> = [
  { key: 'created', label: 'Leads' },
  { key: 'lodged', label: 'Submissions' },
  { key: 'approved', label: 'Approvals' },
  { key: 'settled', label: 'Settlements' },
];

/**
 * Every line of Deal mix copy that names the stage. Settlements is word for word the copy from
 * before the choice existed, so the default section reads exactly as it did.
 */
const MIX_STAGE_COPY: Record<MixStage, {
  title: string; deals: string; verb: string; basis: string; pill: string; tile: string; none: string;
}> = {
  created: {
    title: 'Deal mix of leads, by deal $ and count', deals: 'leads', verb: 'were created',
    basis: 'deal value', pill: 'leads', tile: 'Leads', none: 'No leads were created in this window.',
  },
  lodged: {
    title: 'Deal mix at submission, by deal $ and count', deals: 'submitted deals', verb: 'were lodged with a lender',
    basis: 'deal value', pill: 'submitted', tile: 'Submissions', none: 'No deals were lodged in this window.',
  },
  approved: {
    title: 'Deal mix at approval, by deal $ and count', deals: 'approved deals', verb: 'were approved',
    basis: 'deal value', pill: 'approved', tile: 'Approvals', none: 'No deals were approved in this window.',
  },
  settled: {
    title: 'Deal mix at settlement, by settled $ and count', deals: 'settled deals', verb: 'settled',
    basis: 'settled deal value', pill: 'settled', tile: 'Settlements', none: 'No deals settled in this window.',
  },
};

/** The stage a payload describes. A payload from before the choice carries none and is settlements. */
const stageOf = (mix: Pick<DealMixData, 'stage'>): MixStage => mix.stage ?? DEFAULT_MIX_STAGE;

/** "lender recorded on 12 of 40 settled deals". Every wheel states it; below half, prominently. */
const coverageLine = (wheel: MixWheel, stage: MixStage = DEFAULT_MIX_STAGE): string =>
  `${wheel.title.toLowerCase()} recorded on ${num(wheel.recordedDeals)} of ${num(wheel.settledDeals)} ${MIX_STAGE_COPY[stage].deals}`;

/**
 * A wheel whose dimension is recorded on NONE of the deals that settled in the window. Drawn, it
 * is a full circle of Not recorded, which says nothing about the mix and reads as though the chart
 * failed. Partial coverage is different: that wheel is drawn, with its coverage badge.
 */
export const isZeroCoverageWheel = (w: MixWheel): boolean =>
  w.available && w.settledDeals > 0 && w.recordedDeals === 0;

/** A wheel that has something to draw: its input arrived and at least one deal carries the field. */
export const isDrawableWheel = (w: MixWheel): boolean => w.available && !isZeroCoverageWheel(w);

/** Why a zero-coverage wheel is not drawn, in one line. Loan splits carry purpose and type. */
const zeroCoverageNeeds = (w: MixWheel, stage: MixStage = DEFAULT_MIX_STAGE): string =>
  w.dimension === 'loan_purpose' || w.dimension === 'transaction_type'
    ? `The Sales export carries no loan splits for these deals, so ${w.title.toLowerCase()} is recorded on 0 of ${num(w.settledDeals)} ${MIX_STAGE_COPY[stage].deals}.`
    : `${w.title} is recorded on 0 of ${num(w.settledDeals)} ${MIX_STAGE_COPY[stage].deals} in this window.`;

/**
 * The wheels that cannot be drawn, left out of the wheel grid and named in the Pipeline tab's
 * "Coming when the data is available" list instead (founder, 1 Oct 2026). Two reasons:
 *   - waiting: the dimension lives in the Sales export and no Sales upload matches this snapshot.
 *     An empty wheel would read as "no lead sources", which is not what happened.
 *   - zero coverage: the input arrived but no settled deal in the window carries the field.
 */
export const waitingWheelItems = (mix: DealMixData): ComingSoonItem[] =>
  mix.wheels
    .filter((w) => !isDrawableWheel(w))
    .map((w) => ({
      section: `Deal mix: ${w.title}`,
      needs: w.available
        ? zeroCoverageNeeds(w)
        : 'The Sales export taken on the same day as this Performance export. This wheel is read from Sales.',
      from: 'Evoke',
    }));

/**
 * One colour per category for the life of a wheel (founder, 5 Oct 2026: the wheel colours were too
 * alike). Distinct CHART_PALETTE hues, handed out largest first by settled value, so the biggest
 * slice is always blue and a category keeps its colour when the $ / # toggle reorders the slices.
 * Not recorded is always grey: it is an absence, not a category.
 */
export function wheelColours(wheel: Pick<MixWheel, 'segments'>): (s: { label: string; notRecorded: boolean }) => string {
  const byValue = foldSegments(wheel.segments, 'value').filter((s) => !s.notRecorded);
  const assigned = new Map(byValue.map((s, i) => [s.label, chartColour(i)]));
  let next = byValue.length;
  return (s) => {
    if (s.notRecorded) return NOT_RECORDED_COLOUR;
    let colour = assigned.get(s.label);
    if (colour === undefined) {
      // Only an "Other (n)" fold can be new in count order; it takes the next unused hue.
      colour = chartColour(next++);
      assigned.set(s.label, colour);
    }
    return colour;
  };
}

export const WheelCard: React.FC<{ wheel: MixWheel; mode: Mode; stage?: MixStage }> = ({ wheel, mode, stage = DEFAULT_MIX_STAGE }) => {
  // A waiting or zero-coverage wheel is never drawn; DealMixSection leaves it out and the tab lists it.
  if (!isDrawableWheel(wheel)) return null;
  const segments = foldSegments(wheel.segments, mode === '$' ? 'value' : 'count');
  const total = segments.reduce((n, s) => n + (mode === '$' ? s.valueCents : s.count), 0);
  const colours = wheelColours(wheel);
  const coloured = segments.map((s) => ({
    ...s,
    colour: colours(s),
    value: mode === '$' ? s.valueCents : s.count,
  }));
  return (
    <div className={`${SURFACE} p-4`} data-testid={`wheel-${wheel.dimension}`}>
      <div className="flex flex-wrap items-center gap-2">
        <h4 className="text-[0.8125rem] font-semibold text-gray-100">{wheel.title}</h4>
        {wheel.lowCoverage && <Pill tone="warn">{wheel.coveragePct}% coverage</Pill>}
      </div>
      <p className={`mt-1 text-xs ${wheel.lowCoverage ? 'font-semibold text-amber-300' : 'text-gray-400'}`}>
        {coverageLine(wheel, stage)}
        {wheel.lowCoverage && '. The wheel shows the rest as Not recorded rather than guessing it.'}
      </p>
      {wheel.settledDeals === 0 ? (
        <p className="mt-3 text-sm text-gray-400">{MIX_STAGE_COPY[stage].none}</p>
      ) : (
        <div className="mt-3 flex flex-wrap items-center gap-4">
          <Donut
            data={coloured.filter((s) => s.value > 0)}
            format={(v: number) => (mode === '$' ? money(v) : num(v))}
            centre={mode === '$' ? money(total) : `${num(total)} deals`}
          />
          <ul className="min-w-[10rem] flex-1 space-y-1 text-xs">
            {coloured.map((s) => (
              <li key={s.label} className="flex items-baseline justify-between gap-3">
                <span className={`flex items-center gap-2 ${s.notRecorded ? 'text-gray-500' : 'text-gray-300'}`}>
                  <i className="inline-block h-2 w-2 shrink-0 rounded-sm" style={{ background: s.colour }} />
                  {s.label}
                </span>
                <span className="whitespace-nowrap tabular-nums text-gray-200">
                  {mode === '$' ? money(s.valueCents) : num(s.count)}
                  <span className="ml-1.5 text-gray-500">
                    {total > 0 ? `${Math.round(((mode === '$' ? s.valueCents : s.count) / total) * 100)}%` : ''}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

/** The stage choice, styled as the tab's Deal type and Label filters. */
export const MixStageSelect: React.FC<{ stage: MixStage; onChange: (stage: MixStage) => void }> = ({ stage, onChange }) => (
  <label className="flex items-center gap-2 text-xs text-gray-400">
    Stage
    <select
      aria-label="Deal mix stage"
      value={stage}
      onChange={(e) => onChange(e.target.value as MixStage)}
      className="rounded-md border border-gray-600 bg-gray-900 px-2.5 py-1.5 text-[0.8125rem] text-gray-200"
    >
      {MIX_STAGE_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
    </select>
  </label>
);

export const DealMixSection: React.FC<{
  /** Null while the chosen stage loads; the panel and its stage choice stay on screen. */
  mix: DealMixData | null;
  salesAsAtDate: string | null;
  /** The stage chosen on screen. Without `onStageChange` there is no choice and no dropdown. */
  stage?: MixStage;
  onStageChange?: (stage: MixStage) => void;
  /** What fills the panel while `mix` is null: the loading, failure or refusal state. */
  pending?: React.ReactNode;
} & PanelScope> = ({ mix, salesAsAtDate, stage: chosen, onStageChange, pending, accent, chip }) => {
  // Dollars first, as on Deal movements. One toggle for the section: wheels each on their own
  // unit could not be compared across (agreed with Evoke, 25 Sep 2026).
  const [mode, setMode] = useState<Mode>('$');
  const select = onStageChange
    ? <MixStageSelect stage={chosen ?? (mix ? stageOf(mix) : DEFAULT_MIX_STAGE)} onChange={onStageChange} />
    : null;
  if (mix === null) {
    // NEVER ANOTHER STAGE'S WHEELS under this stage's title: nothing is drawn until its own figures land.
    const copy = MIX_STAGE_COPY[chosen ?? DEFAULT_MIX_STAGE];
    return (
      <Panel title={copy.title} icon={PieIcon} accent={accent} badge={<>{chip}{select}</>}>
        {pending}
      </Panel>
    );
  }
  // The copy describes the figures on screen, so it follows the payload's stage, not the choice.
  const stage = stageOf(mix);
  const copy = MIX_STAGE_COPY[stage];
  // Only the wheels that can be drawn, so the grid re-flows with no gap where a listed wheel was.
  const shown = mix.wheels.filter(isDrawableWheel);
  if (shown.length === 0) return null;
  // The tab's "Coming when the data is available" list is built from settlements. For another
  // stage, a wheel no deal in that stage carries is named here instead, so it is never just absent.
  const notDrawn = stage === DEFAULT_MIX_STAGE ? [] : mix.wheels.filter(isZeroCoverageWheel);
  return (
    <Panel
      title={copy.title}
      icon={PieIcon}
      accent={accent}
      badge={<>{chip}{select}<Pill><PieIcon className="h-3 w-3" />{num(mix.settled.count)} {copy.pill} &middot; {money(mix.settled.valueCents)}</Pill><UnitToggle mode={mode} onChange={setMode} label="Show deal mix as" /></>}
      blurb={
        <>
          The deals that {copy.verb} between <WindowLine from={mix.from} to={mix.to} />, cut {shown.length === 1 ? 'one way' : `${shown.length} ways`} by
          {' '}{copy.basis} and deal count. These are the same deals as {copy.tile} in Deal
          movements. Not the Dashboard&apos;s commission pies: nothing here is commission.
          {salesAsAtDate !== null && <> Lead source, loan purpose and transaction type are read from the Sales export of {dayLabel(salesAsAtDate)}.</>}
        </>
      }
    >
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {shown.map((w) => <WheelCard key={w.dimension} wheel={w} mode={mode} stage={stage} />)}
      </div>
      {notDrawn.length > 0 && (
        <p className="mt-3 text-xs text-gray-500" data-testid="deal-mix-not-drawn">
          Not drawn: {notDrawn.map((w) => zeroCoverageNeeds(w, stage)).join(' ')}
        </p>
      )}
      {shown.some((w) => w.dimension === 'loan_purpose' || w.dimension === 'transaction_type') && (
        <p className="mt-3 text-xs text-gray-500">
          Loan purpose and transaction type come from each deal&apos;s loan splits. Where the splits on
          one deal disagree the deal reads Mixed rather than being assigned to one of them.
        </p>
      )}
    </Panel>
  );
};

// ---------------------------------------------------------------------------------------------
// Pipeline velocity
// ---------------------------------------------------------------------------------------------

const velocityText = (cents: number | null): string => (cents === null ? '-' : `${money(cents)}/day`);

const cycleBehind = (deals: number): string => `median of ${num(deals)} ${deals === 1 ? 'deal' : 'deals'} with both dates`;

const FormulaTile: React.FC<{ label: string; value: string; sub: React.ReactNode; result?: boolean }> = ({ label, value, sub, result }) => (
  <div className={`rounded-lg border p-3 ${result ? 'border-blue-500/40 bg-blue-600/10' : 'border-gray-700/70 bg-gray-900/50'}`}>
    <div className="text-[0.625rem] font-semibold uppercase tracking-wider text-gray-400">{label}</div>
    <div className={`mt-1 text-2xl font-semibold tabular-nums leading-none ${result ? 'text-blue-300' : 'text-gray-100'}`}>{value}</div>
    <div className="mt-1 text-[0.6875rem] leading-snug text-gray-500">{sub}</div>
  </div>
);

const Op: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="self-center text-lg font-semibold text-gray-500" aria-hidden="true">{children}</span>
);

export const PipelineVelocityPanel: React.FC<{ velocity: VelocityData } & PanelScope> = ({ velocity, accent, chip }) => {
  const b = velocity.business;
  // The server measures the headline on a fixed trailing year (salesAnalytics.ts
  // trailingVelocityWindow): lodged to settled takes months, so a FYTD cohort has nothing settled.
  // An older payload without `basis` was measured on the selected period and keeps that wording.
  const trailing = velocity.basis === 'trailing_year';
  const scope = trailing ? 'the 12 months' : 'the window';
  return (
    <Panel
      title="Pipeline velocity"
      icon={Gauge}
      accent={accent}
      badge={<>{chip}<Pill tone="brand"><Gauge className="h-3 w-3" />settlement volume per day, not revenue</Pill></>}
      blurb={
        <>
          {trailing ? (
            <>
              Submissions lodged in the 12 months to {dayLabel(velocity.to)} (<WindowLine from={velocity.from} to={velocity.to} />),
              whatever period is selected above, times the
            </>
          ) : (
            <>Submissions lodged between <WindowLine from={velocity.from} to={velocity.to} />, times the</>
          )}{' '}
          share of them that have settled, times their average settled deal, divided by their median
          days from lodged to settled. All four inputs are measured on the same lodged deals.
        </>
      }
    >
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto_1fr_auto_1fr_auto_1fr_auto_1fr]">
        <FormulaTile label="Submissions lodged" value={num(b.lodgedDeals)} sub={`dated lodged in ${scope}`} />
        <Op>&times;</Op>
        <FormulaTile label="Win rate" value={bpToPct(b.winRateBp)} sub={`${num(b.settledDeals)} settled of ${num(b.lodgedDeals)} lodged`} />
        <Op>&times;</Op>
        <FormulaTile label="Avg settled deal" value={money(b.avgSettledValueCents)} sub={`over ${num(b.valuedSettledDeals)} settled deals with a value`} />
        <Op>&divide;</Op>
        <FormulaTile
          label="Cycle time"
          value={b.cycleMedianDays === null ? '-' : `${num(b.cycleMedianDays)}d`}
          sub={`median lodged to settled, ${num(b.cycleMeasuredDeals)} deals with both dates`}
        />
        <Op>=</Op>
        <FormulaTile label="Pipeline velocity" value={velocityText(b.velocityCentsPerDay)} sub="settlement volume per day, not revenue" result />
      </div>

      {/* Said once, plainly, rather than as a column of dashes and "n=0" a reader has to decode. */}
      {b.cycleMeasuredDeals === 0 && (
        <p className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3.5 py-2.5 text-[0.8125rem] text-amber-200">
          Cycle time, and so velocity, cannot be measured for {trailing ? 'these 12 months' : 'this window'}: none of its {num(b.lodgedDeals)} lodged
          deals carries a settled date on or after its lodged date.
          {b.cycleDisorderedDeals > 0 && ` ${num(b.cycleDisorderedDeals)} settled deals are dated before their lodgement, which points at the dates in the export rather than at the deals.`}
          {' '}Win rate and average settled deal still read normally.
        </p>
      )}

      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr>
              <th className={TH}>Broker</th>
              <th className={`${TH} text-right`}>Submissions</th>
              <th className={`${TH} text-right`}>Win rate</th>
              <th className={`${TH} text-right`}>Avg settled deal</th>
              <th className={`${TH} text-right`}>Cycle time</th>
              <th className={`${TH} text-right`}>Velocity</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700/50 text-gray-300">
            {[b, ...velocity.brokers].map((r: VelocityRow, i) => (
              <tr key={r.adviserKey} className={i === 0 ? 'font-semibold text-gray-100' : ''}>
                <td className="px-3 py-2">{r.adviserName}</td>
                <td className={`${TD} text-right`}>{num(r.lodgedDeals)}</td>
                {/* The deals behind each figure live in the hover title and in screen-reader text, not
                    on the cell, as in the conversion matrix (founder, 5 Oct 2026: no visible "n="). */}
                <td className={`${TD} text-right`} title={`${num(r.settledDeals)} settled of ${num(r.lodgedDeals)} lodged`}>
                  {bpToPct(r.winRateBp)}
                  <span className="sr-only">, {num(r.settledDeals)} settled of {num(r.lodgedDeals)} lodged</span>
                </td>
                <td className={`${TD} text-right`}>{money(r.avgSettledValueCents)}</td>
                {r.cycleMedianDays === null ? (
                  <td className={`${TD} text-right`}>
                    <span className="text-gray-500">not measurable</span>
                  </td>
                ) : (
                  <td className={`${TD} text-right`} title={cycleBehind(r.cycleMeasuredDeals)}>
                    {`${num(r.cycleMedianDays)}d`}
                    <span className="sr-only">, {cycleBehind(r.cycleMeasuredDeals)}</span>
                  </td>
                )}
                <td className={`${TD} text-right`}>{velocityText(r.velocityCentsPerDay)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Honesty>
        {trailing
          ? 'Measured on the deals lodged in the 12 months to the snapshot date, not the selected period: lodged to settled takes months, so a younger window would read low on every figure. The most recent of these deals have still had less time to settle, which lowers the win rate and shortens the cycle time together.'
          : 'Measured on the deals lodged in the window, so a recent window reads low: its deals have had less time to settle, which lowers the win rate and shortens the cycle time together.'}
        Commercial deals often carry no lodged date and are not in these figures. A cycle time of
        zero days, or a window with nothing settled yet, shows no velocity rather than a guess.
        {b.cycleDisorderedDeals > 0 && ` ${num(b.cycleDisorderedDeals)} settled deals dated before their lodgement are left out of the cycle time.`}
      </Honesty>
    </Panel>
  );
};

// ---------------------------------------------------------------------------------------------
// Pipeline velocity trend
// ---------------------------------------------------------------------------------------------

export const VelocityTrendPanel: React.FC<{ trend: VelocityTrendData } & PanelScope> = ({ trend, accent, chip }) => {
  const months = trend.months.map((m) => ({ key: m, label: monthLabel(m) }));
  const series = [
    { key: 'business', name: 'All brokers (business)', colour: '#e5e7eb', values: trend.business },
    ...trend.brokers.map((b, i) => ({ key: `b${i}`, name: b.adviserName, colour: chartColour(i), values: b.values })),
  ];
  const anyValue = series.some((s) => s.values.some((v) => v !== null));
  return (
    <Panel
      title="Pipeline velocity trend, 12 months: business and brokers"
      icon={TrendingUp}
      accent={accent}
      badge={<>{chip}<Pill><TrendingUp className="h-3 w-3" />monthly, lodged cohort</Pill></>}
      blurb="Pipeline velocity for the deals lodged in each month, for the whole business and for each broker, read from the dates in this snapshot. A gap is a month with nothing to divide: nothing lodged, nothing settled yet, or no measurable cycle time."
    >
      {!anyValue ? (
        <p className="text-sm text-gray-400">No month in the last twelve has a measurable velocity in this scope.</p>
      ) : (
        <>
          {/* Drawn like the Deal movements trend (founder, 5 Oct 2026), with recharts' own legend. */}
          <VelocityTrendChart months={trend.months} series={series} />
          <details className="mt-3">
            <summary className="cursor-pointer text-xs text-gray-400 hover:text-gray-200">Show the monthly figures</summary>
            <div className="mt-2 overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr>
                    <th className={TH}>Line</th>
                    {months.map((m) => <th key={m.key} className={`${TH} text-right`}>{m.label}</th>)}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700/50 text-gray-300">
                  {series.map((s) => (
                    <tr key={s.key}>
                      <td className="whitespace-nowrap px-3 py-1.5">{s.name}</td>
                      {s.values.map((v, i) => (
                        <td key={months[i].key} className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums">{velocityText(v)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        </>
      )}
    </Panel>
  );
};

// ---------------------------------------------------------------------------------------------
// Pipeline forecast
// ---------------------------------------------------------------------------------------------

const firstMonthLabel = (m: { month: string; from: string }, i: number): string =>
  (i === 0 ? `${monthLabel(m.month)} (from ${dayLabel(m.from).split(' ').slice(0, 2).join(' ')})` : monthLabel(m.month));

export const ForecastPanel: React.FC<{ forecast: ForecastData } & PanelScope> = ({ forecast, accent, chip }) => {
  const [mode, setMode] = useState<Mode>('#');
  const { anchors, totals } = forecast;
  return (
    <Panel
      title="Pipeline forecast, next 4 months"
      icon={Telescope}
      accent={accent}
      badge={<>{chip}<UnitToggle mode={mode} onChange={setMode} label="Show forecast as" /></>}
      blurb={
        <>
          Settlements projected from deals already at submission or later, conditional approval
          excluded. Method: each deal&apos;s own lodged date (submitted stages) or unconditional
          approval date (later stages), plus the median days from that milestone to settlement
          measured on this snapshot&apos;s settled deals: {anchors.lodged.medianDays === null ? 'not measurable' : `${num(anchors.lodged.medianDays)} days`} from lodged
          ({num(anchors.lodged.measuredDeals)} deals), {anchors.approved.medianDays === null ? 'not measurable' : `${num(anchors.approved.medianDays)} days`} from approval
          ({num(anchors.approved.measuredDeals)} deals).
        </>
      }
    >
      {forecast.stages.length === 0 ? (
        <p className="text-sm text-gray-400">No deal in flight is at submission or later in this scope.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr>
                <th className={TH}>Stage</th>
                <th className={`${TH} text-right`}>Deals today</th>
                <th className={`${TH} text-right`}>$ today</th>
                <th className={`${TH} text-right`}>Days to settle</th>
                {forecast.months.map((m, i) => <th key={m.month} className={`${TH} text-right`}>{firstMonthLabel(m, i)}</th>)}
                <th className={`${TH} text-right`}>Later</th>
                <th className={`${TH} text-right`}>Past typical timing</th>
                <th className={`${TH} text-right`}>Cannot project</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/50 text-gray-300">
              <tr className="font-semibold text-gray-100">
                <td className="px-3 py-2">All stages</td>
                <td className={`${TD} text-right`}>{num(totals.deals.count)}</td>
                <td className={`${TD} text-right`}>{money(totals.deals.valueCents)}</td>
                <td className={`${TD} text-right`}>-</td>
                {totals.months.map((c, i) => <td key={forecast.months[i].month} className={`${TD} text-right`}>{cellText(c, mode)}</td>)}
                <td className={`${TD} text-right`}>{cellText(totals.later, mode)}</td>
                <td className={`${TD} text-right text-amber-300`}>{cellText(totals.pastTypical, mode)}</td>
                <td className={`${TD} text-right text-gray-500`}>{cellText(totals.unprojectable, mode)}</td>
              </tr>
              {forecast.stages.map((s) => (
                <tr key={s.canonicalKey}>
                  <td className="px-3 py-2">{s.label}</td>
                  <td className={`${TD} text-right`}>{num(s.deals.count)}</td>
                  <td className={`${TD} text-right`}>{money(s.deals.valueCents)}</td>
                  <td className={`${TD} text-right`}>
                    {/* One line: the days and what they are counted from belong together. */}
                    <span className="whitespace-nowrap">
                      {s.medianDaysToSettle === null ? '-' : `${num(s.medianDaysToSettle)}d`}
                      <span className="ml-1 text-xs text-gray-500">from {s.anchor === 'lodged' ? 'lodged' : 'approval'}</span>
                    </span>
                  </td>
                  {s.months.map((c, i) => <td key={forecast.months[i].month} className={`${TD} text-right`}>{cellText(c, mode)}</td>)}
                  <td className={`${TD} text-right`}>{cellText(s.later, mode)}</td>
                  <td className={`${TD} text-right text-amber-300`}>{cellText(s.pastTypical, mode)}</td>
                  <td className={`${TD} text-right text-gray-500`}>{cellText(s.unprojectable, mode)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Honesty>
        A projection, not a booking: it assumes each deal takes the typical time from its milestone
        to settlement. Past typical timing means the projection fell before the snapshot date; those
        deals are not pushed into next month, because a late deal is not a forecast. The export does
        not date entry into Documents issued or Settlement booked, so every stage after approval
        shares the approval median.
        {totals.fromStageEntered > 0 && ` ${num(totals.fromStageEntered)} deals carry no milestone date and are projected from the date they entered their current stage, which can only place them later.`}
      </Honesty>
    </Panel>
  );
};

// ---------------------------------------------------------------------------------------------
// Planned settlements
// ---------------------------------------------------------------------------------------------

const TIER: Record<SettlementTier, { name: string; colour: string }> = {
  committed: { name: 'Committed', colour: CHART_PALETTE[2] },
  likely: { name: 'Likely', colour: CHART_PALETTE[0] },
  pipeline: { name: 'Pipeline', colour: CHART_PALETTE[3] },
};

export const PlannedSettlementsPanel: React.FC<{ planned: PlannedData } & PanelScope> = ({ planned, accent, chip }) => {
  const [mode, setMode] = useState<Mode>('$');
  const monthNames = planned.months.map((m, i) => firstMonthLabel(m, i));
  return (
    <Panel
      title="Planned settlements by month"
      icon={CalendarClock}
      accent={accent}
      badge={<>{chip}<Pill><CalendarClock className="h-3 w-3" />Salestrekker estimated settlement date</Pill><UnitToggle mode={mode} onChange={setMode} label="Show planned settlements as" /></>}
      blurb="Built from the estimated settlement date brokers keep on each deal, split by how far along the deal is, so a booked settlement never reads the same as a hopeful one. Deals whose estimate has already passed by the snapshot date are listed separately instead of being counted in a month."
    >
      {/* One column, two rows (founder, 29 Sep 2026): the chart, then the table at full width, so
          the month headings and tier descriptions never wrap into a narrow half. */}
      <div className="grid gap-4">
        <div>
          <StackedMonths
            months={monthNames}
            unit={mode === '$' ? 'cents' : 'count'}
            stacks={planned.tiers.map((t) => ({
              key: t.tier,
              name: TIER[t.tier].name,
              colour: TIER[t.tier].colour,
              values: t.months.map((c) => (mode === '$' ? c.valueCents : c.count)),
            }))}
          />
          <div className="mt-1 flex flex-wrap gap-x-4 text-xs text-gray-300">
            {planned.tiers.map((t) => (
              <span key={t.tier} className="flex items-center gap-1.5">
                <i className="inline-block h-2 w-2 rounded-sm" style={{ background: TIER[t.tier].colour }} />
                {TIER[t.tier].name}
              </span>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr>
                <th className={TH}>Tier</th>
                {monthNames.map((m) => <th key={m} className={`${TH} text-right`}>{m}</th>)}
                <th className={`${TH} text-right`}>Later</th>
                <th className={`${TH} text-right`}>Estimate passed</th>
                <th className={`${TH} text-right`}>No estimate</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/50 text-gray-300">
              {planned.tiers.map((t) => (
                <tr key={t.tier}>
                  <td className="px-3 py-2">
                    <span className="flex items-center gap-2">
                      <i className="inline-block h-2 w-2 rounded-sm" style={{ background: TIER[t.tier].colour }} />
                      {TIER[t.tier].name}
                    </span>
                    <span className="block text-[0.6875rem] text-gray-500">
                      {t.stageLabels.length > 0 ? t.stageLabels.join(', ') : 'No deals in these stages'}
                    </span>
                  </td>
                  {t.months.map((c, i) => (
                    <td key={monthNames[i]} className={`${TD} text-right`}>
                      {cellText(c, mode)}<span className="block text-[0.6875rem] text-gray-500">{cellSub(c, mode)}</span>
                    </td>
                  ))}
                  <td className={`${TD} text-right`}>{cellText(t.later, mode)}</td>
                  <td className={`${TD} text-right text-amber-300`}>
                    {cellText(t.estimatePassed, mode)}<span className="block text-[0.6875rem] text-gray-500">{cellSub(t.estimatePassed, mode)}</span>
                  </td>
                  <td className={`${TD} text-right text-gray-500`}>{cellText(t.noEstimate, mode)}</td>
                </tr>
              ))}
              <tr className="font-semibold text-gray-100">
                <td className="px-3 py-2">All tiers</td>
                {planned.totals.months.map((c, i) => <td key={monthNames[i]} className={`${TD} text-right`}>{cellText(c, mode)}</td>)}
                <td className={`${TD} text-right`}>{cellText(planned.totals.later, mode)}</td>
                <td className={`${TD} text-right text-amber-300`}>{cellText(planned.totals.estimatePassed, mode)}</td>
                <td className={`${TD} text-right text-gray-500`}>{cellText(planned.totals.noEstimate, mode)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-5">
        <h4 className="text-xs font-semibold text-gray-200">Estimates needing a date, by broker</h4>
        <p className="mb-2 mt-1 text-xs text-gray-400">
          Deals in flight whose estimated settlement date had passed by the snapshot date. Late stage
          means the deal is already at submission or beyond, where the date matters most.
        </p>
        {planned.needingDate.length === 0 ? (
          <p className="text-sm text-gray-400">No deal in flight carries an estimate that has passed.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr>
                  <th className={TH}>Broker</th>
                  <th className={`${TH} text-right`}>Estimate passed</th>
                  <th className={`${TH} text-right`}>Of which late stage</th>
                  <th className={`${TH} text-right`}>Median days past</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/50 text-gray-300">
                <tr className="font-semibold text-gray-100">
                  <td className="px-3 py-2">All brokers</td>
                  <td className={`${TD} text-right`}>{num(planned.needingDateTotal.passed.count)}<span className="ml-1 text-xs text-gray-500">{money(planned.needingDateTotal.passed.valueCents)}</span></td>
                  <td className={`${TD} text-right`}>{num(planned.needingDateTotal.lateStage)}</td>
                  <td className={`${TD} text-right`}>{planned.needingDateTotal.medianDaysPast === null ? '-' : `${num(planned.needingDateTotal.medianDaysPast)}d`}</td>
                </tr>
                {planned.needingDate.map((r) => (
                  <tr key={r.adviserKey}>
                    <td className="px-3 py-2">{r.adviserName}</td>
                    <td className={`${TD} text-right`}>{num(r.passed.count)}<span className="ml-1 text-xs text-gray-500">{money(r.passed.valueCents)}</span></td>
                    <td className={`${TD} text-right`}>{num(r.lateStage)}</td>
                    <td className={`${TD} text-right`}>{r.medianDaysPast === null ? '-' : `${num(r.medianDaysPast)}d`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Honesty>
        Only as good as brokers keep the date. On a settled deal the estimate is usually overwritten
        with the actual day, so its accuracy cannot be read back from one export; each weekly upload
        keeps that week&apos;s estimate, and slippage by broker becomes measurable as uploads build up.
      </Honesty>
    </Panel>
  );
};

// ---------------------------------------------------------------------------------------------
// States shared by both placements
// ---------------------------------------------------------------------------------------------

/**
 * Loading, failure, refusal and no snapshot, for a section that has not got its data. Never an
 * empty section: a failed load that rendered as empty wheels would say nothing settled.
 */
export const SalesAnalyticsState: React.FC<{
  title: string;
  response: SalesAnalyticsResponse | null;
  loading: boolean;
  error: string | null;
  waitingForDates?: boolean;
  onRetry?: () => void;
}> = ({ title, response, loading, error, waitingForDates, onRetry }) => {
  let body: React.ReactNode;
  if (waitingForDates) body = 'Apply the custom dates above to fill this section.';
  else if (error) {
    body = (
      <>
        {title} could not be loaded: {error}
        {onRetry && (
          <button type="button" onClick={onRetry} className="ml-3 rounded-md border border-amber-500/40 px-2 py-0.5 text-xs font-semibold text-amber-100 hover:bg-amber-500/10">
            Try again
          </button>
        )}
      </>
    );
  } else if (loading || response === null) {
    return (
      <div aria-label={`Loading ${title.toLowerCase()}`}>
        <SkeletonTable rows={4} columns={4} />
      </div>
    );
  }
  else if (response.status === 'invalid_request') body = `That reporting window could not be used: ${response.reason}`;
  else if (response.status === 'no_snapshot') body = 'No Performance snapshot has been committed yet.';
  else return null;
  return (
    <div className={`${SURFACE} px-5 py-4 text-sm ${error || response?.status === 'invalid_request' ? 'text-amber-300' : 'text-gray-400'}`}>
      {body}
    </div>
  );
};
